import { useState, useCallback } from 'react';
import { Modal, Header } from '@trinity/chat-components/components/Modal';
import { cx } from '@trinity/chat-components/utils';
import { ImageCropper } from './ImageCropper';
import { validateFile } from './utils';
import styles from './style/ImageCropUpload.module.css';

type Props = {
  // Required
  open: boolean;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void> | void;

  // Optional - Customization
  title?: string;
  currentImageUrl?: string;
  aspectRatio?: number;
  strictAspectRatio?: boolean;
  isSaving?: boolean;

  // Optional - Validation
  acceptedFileTypes?: string[];
  maxFileSizeMB?: number;

  // Optional - Accessibility
  'aria-label'?: string;

  // Optional - Styling
  classes?: {
    root?: string;
    modal?: string;
    header?: string;
  };
};

export const ImageCropUpload = ({
  open,
  onClose,
  onSave,
  title = 'Upload Image',
  currentImageUrl,
  aspectRatio = 1,
  strictAspectRatio = true,
  isSaving = false,
  acceptedFileTypes = ['image/jpeg', 'image/png'],
  maxFileSizeMB = 25,
  'aria-label': ariaLabel = 'Image crop and upload dialog',
  classes = {},
}: Props) => {
  const [fileError, setFileError] = useState<string>();

  const handleFileSelect = useCallback(
    (file: File) => {
      const validation = validateFile(file, {
        validTypes: acceptedFileTypes,
        maxSizeMB: maxFileSizeMB,
      });

      if (!validation.valid) {
        setFileError(validation.error);
      } else {
        setFileError(undefined);
      }
    },
    [acceptedFileTypes, maxFileSizeMB]
  );

  const handleSave = useCallback(
    async (data: { blob: Blob }) => {
      await onSave(data.blob);
    },
    [onSave]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      classes={{
        root: cx(styles.modalRoot, classes.modal),
      }}
      aria-label={ariaLabel}
    >
      <div className={cx(styles.root, classes.root)}>
        <div className={styles.wrap}>
          <div className={styles.main}>
            <Header className={classes.header}>{title}</Header>
            <ImageCropper
              src={currentImageUrl}
              aspectRatio={aspectRatio}
              strictAspectRatio={strictAspectRatio}
              isSaving={isSaving}
              onFileSelect={handleFileSelect}
              fileError={fileError}
              onSave={handleSave}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

ImageCropUpload.displayName = 'ImageCropUpload';
